import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import {
  getMyBranding,
  mapEffectiveBranding,
  type EffectiveBranding,
} from '../modules/platform/api/branding';
import { platformBrandingFallback } from '../modules/platform/api/branding.types';
import {
  isTenantVisualsExpected,
  resolveCurrentTenantId,
} from '../utils/tenantVisualExpectation';
import {
  applyBrandTheme,
  applyCachedBrandThemeOnBoot,
  clearBrandColorCache,
  emptyTenantBrandingShell,
  fallbackPrimary,
  persistSchoolBranding,
  readCachedSchoolBranding,
  type CachedSchoolBranding,
} from '../utils/brandTheme';

interface BrandThemeContextValue {
  themeColor: string;
  logoUrl: string;
  displayName: string;
  displayNameTa: string;
  loading: boolean;
  /** False while tenant-specific branding is still unresolved. */
  brandingReady: boolean;
  tenantVisualsExpected: boolean;
  tenantId: string | null;
  refreshBrandTheme: () => Promise<void>;
  setBrandThemeColor: (hex: string | null | undefined) => void;
  applyBranding: (branding: EffectiveBranding) => void;
}

const BrandThemeContext = createContext<BrandThemeContextValue | undefined>(undefined);

function brandingFromEffective(data: EffectiveBranding, tenantId: string | null): CachedSchoolBranding {
  const mapped = mapEffectiveBranding(data);
  return tenantId ? { ...mapped, tenantId } : mapped;
}

function applyBrandingToDom(branding: CachedSchoolBranding, tenantId: string | null): string {
  const applied = applyBrandTheme(branding.themeColor);
  persistSchoolBranding({ ...branding, themeColor: applied }, tenantId ?? branding.tenantId);
  return applied;
}

function applyPlatformFallbackBranding(): CachedSchoolBranding {
  const fallback = brandingFromEffective(platformBrandingFallback(), null);
  const appliedTheme = applyBrandingToDom(fallback, null);
  return { ...fallback, themeColor: appliedTheme };
}

async function fetchAndApplyBranding(tenantId: string | null): Promise<CachedSchoolBranding> {
  const data = await getMyBranding();
  const branding = brandingFromEffective(data, tenantId);
  applyBrandingToDom(branding, tenantId);
  return branding;
}

function initialBrandingForScope(
  tenantId: string | null,
  tenantVisualsExpected: boolean,
): { branding: CachedSchoolBranding; brandingReady: boolean } {
  if (tenantVisualsExpected && tenantId) {
    const cached = readCachedSchoolBranding(tenantId);
    if (cached?.displayName && cached.logoUrl) {
      return { branding: cached, brandingReady: true };
    }
    return { branding: emptyTenantBrandingShell(), brandingReady: false };
  }

  const cached = readCachedSchoolBranding(null);
  if (cached?.displayName && cached.logoUrl && !cached.tenantId) {
    return { branding: cached, brandingReady: true };
  }
  const fallback = brandingFromEffective(platformBrandingFallback(), null);
  return { branding: { ...fallback, themeColor: fallback.themeColor || fallbackPrimary() }, brandingReady: true };
}

function brandingFromCacheOrPlatform(tenantId: string | null): CachedSchoolBranding {
  const cached = readCachedSchoolBranding(tenantId);
  if (cached?.displayName && cached.logoUrl) {
    const applied = applyBrandingToDom(cached, tenantId);
    return { ...cached, themeColor: applied };
  }
  return applyPlatformFallbackBranding();
}

export const BrandThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const tenantId = useMemo(
    () => resolveCurrentTenantId({ user: authLoading ? undefined : user }),
    [user, authLoading],
  );
  const tenantVisualsExpected = isTenantVisualsExpected(tenantId);

  const bootScope = useMemo(() => {
    const tid = resolveCurrentTenantId({ user: authLoading ? undefined : user });
    const expected = isTenantVisualsExpected(tid);
    return initialBrandingForScope(tid, expected);
  }, [user, authLoading]);

  const [branding, setBranding] = useState<CachedSchoolBranding>(bootScope.branding);
  const [loading, setLoading] = useState(true);
  const [brandingReady, setBrandingReady] = useState(bootScope.brandingReady);
  const prevUserIdRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    const applied = applyCachedBrandThemeOnBoot(tenantId);
    setBranding((prev) => ({ ...prev, themeColor: applied }));
  }, [tenantId]);

  const applyBranding = useCallback(
    (data: EffectiveBranding) => {
      const next = brandingFromEffective(data, tenantId);
      const appliedTheme = applyBrandingToDom(next, tenantId);
      setBranding({ ...next, themeColor: appliedTheme });
      setBrandingReady(true);
    },
    [tenantId],
  );

  const refreshBrandTheme = useCallback(async (): Promise<void> => {
    if (!user) {
      const applied = applyBrandTheme(fallbackPrimary());
      setBranding((prev) => ({ ...prev, themeColor: applied }));
      setBrandingReady(true);
      return;
    }

    try {
      const next = await fetchAndApplyBranding(tenantId);
      setBranding(next);
      setBrandingReady(true);
    } catch {
      setBranding(brandingFromCacheOrPlatform(tenantId));
      setBrandingReady(true);
    }
  }, [user, tenantId]);

  const setBrandThemeColor = useCallback(
    (hex: string | null | undefined) => {
      const applied = applyBrandTheme(hex);
      setBranding((prev) => {
        const next = { ...prev, themeColor: applied };
        persistSchoolBranding(next, tenantId);
        return next;
      });
    },
    [tenantId],
  );

  useEffect(() => {
    if (authLoading) return;

    let cancelled = false;

    void (async () => {
      if (!user) {
        prevUserIdRef.current = null;
        clearBrandColorCache();
        const applied = applyBrandTheme(fallbackPrimary());
        if (!cancelled) {
          setBranding({
            logoUrl: '',
            displayName: '',
            displayNameTa: '',
            themeColor: applied,
          });
          setLoading(false);
          setBrandingReady(true);
        }
        return;
      }

      const userId = user.id ?? null;
      if (userId && userId !== prevUserIdRef.current) {
        clearBrandColorCache();
        prevUserIdRef.current = userId;
        const boot = initialBrandingForScope(tenantId, tenantVisualsExpected);
        if (!cancelled) {
          setBranding(boot.branding);
          setBrandingReady(boot.brandingReady);
        }
      }

      try {
        const next = await fetchAndApplyBranding(tenantId);
        if (!cancelled) {
          setBranding(next);
          setLoading(false);
          setBrandingReady(true);
        }
      } catch {
        if (!cancelled) {
          setBranding(brandingFromCacheOrPlatform(tenantId));
          setLoading(false);
          setBrandingReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, authLoading, tenantId, tenantVisualsExpected]);

  const value = useMemo(
    () => ({
      themeColor: branding.themeColor,
      logoUrl: branding.logoUrl,
      displayName: branding.displayName,
      displayNameTa: branding.displayNameTa,
      loading,
      brandingReady,
      tenantVisualsExpected,
      tenantId,
      refreshBrandTheme,
      setBrandThemeColor,
      applyBranding,
    }),
    [
      branding,
      loading,
      brandingReady,
      tenantVisualsExpected,
      tenantId,
      refreshBrandTheme,
      setBrandThemeColor,
      applyBranding,
    ],
  );

  return <BrandThemeContext.Provider value={value}>{children}</BrandThemeContext.Provider>;
};

export const useBrandTheme = (): BrandThemeContextValue => {
  const context = useContext(BrandThemeContext);
  if (!context) {
    const fallback = applyPlatformFallbackBranding();
    return {
      themeColor: fallback.themeColor,
      logoUrl: fallback.logoUrl,
      displayName: fallback.displayName,
      displayNameTa: fallback.displayNameTa,
      loading: false,
      brandingReady: true,
      tenantVisualsExpected: false,
      tenantId: null,
      refreshBrandTheme: async () => {},
      setBrandThemeColor: (hex: string | null | undefined) => {
        applyBrandTheme(hex);
      },
      applyBranding: () => {},
    };
  }
  return context;
};
